import { useEffect, useRef } from "react";

type Star = { x: number; y: number; radius: number; alpha: number; phase: number };

function makeStars(width: number, height: number): Star[] {
  const count = Math.min(190, Math.max(70, Math.floor((width * height) / 8500)));
  return Array.from({ length: count }, (_, index) => ({
    x: (((index * 73.31) % 100) / 100) * width,
    y: (((index * index * 19.17 + 11) % 100) / 100) * height * 0.82,
    radius: index % 17 === 0 ? 1.5 : index % 5 === 0 ? 1 : 0.6,
    alpha: 0.28 + (index % 7) * 0.08,
    phase: index * 0.37,
  }));
}

export function StarField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let stars: Star[] = [];
    let animationId = 0;

    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const box = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, box.width * ratio);
      canvas.height = Math.max(1, box.height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      stars = makeStars(box.width, box.height);
    };

    const draw = () => {
      const box = canvas.getBoundingClientRect();
      context.clearRect(0, 0, box.width, box.height);
      stars.forEach((star) => {
        const shimmer = media.matches ? 1 : 0.76 + Math.sin(frame * 0.012 + star.phase) * 0.24;
        context.beginPath();
        context.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        context.fillStyle = `rgba(247,243,234,${star.alpha * shimmer})`;
        context.fill();
      });
      frame += 1;
      if (!document.hidden && !media.matches) animationId = requestAnimationFrame(draw);
    };

    const onVisibility = () => {
      cancelAnimationFrame(animationId);
      draw();
    };
    resize();
    draw();
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return <canvas ref={canvasRef} className="star-field" aria-hidden="true" />;
}
